import { Injectable, signal, type Signal, type WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { AuthService } from './auth.service';
import { EmitterService } from './emitter.service';
import { ItemPermissionsService } from './item-permissions.service';
import { FeatureDetail, PermissionAction, type PermissionActionValue } from '../constants';
import { EffectivePermissionEntry } from '../interfaces/auth-permissions.interface';
import { ItemPermissionEntry, ItemPermissionsResponse } from '../interfaces';
import { AllowedOperationsUpdatedEvent } from '../models/events';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly _canManageCompanyUsers: WritableSignal<boolean>;
  /** Reactive `canManageCompanyUsers()` for template gates — refreshed on `AllowedOperationsUpdatedEvent`. */
  readonly canManageCompanyUsersSignal: Signal<boolean>;

  constructor(
    private readonly authService: AuthService,
    private readonly itemPermissionsService: ItemPermissionsService
  ) {
    this._canManageCompanyUsers = signal<boolean>(this.canManageCompanyUsers());
    this.canManageCompanyUsersSignal = this._canManageCompanyUsers.asReadonly();
    EmitterService.get(AllowedOperationsUpdatedEvent)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._canManageCompanyUsers.set(this.canManageCompanyUsers()));
  }

  public prepareSectionAndSubSections(section: { isSectionVisibility?: boolean }): void {
    try {
      //warning: isSectionVisibility true means not visible
      section.isSectionVisibility = false;
    } catch (error) {
      section.isSectionVisibility = true;
    }
  }

  // CORE_COMMENT (2026-08-26) and then the read verb itself (2026-08-31) left the RBAC contract,
  // so the per-item read authorize is gone too — commenting rides on CORE_PROJECT access until
  // the backend exposes a dedicated comment capability. Stays Observable for its consumers.
  public canCommentOnSubsection(projectSubSectionId: string): Observable<boolean> {
    return of(this.hasFeatureAccess(FeatureDetail.CORE_PROJECT));
  }

  /**
   * Direct check against the RBAC effective-permissions matrix: the feature must have
   * access === true and the action must appear in one of its capability groups
   * ("main.actions", "actions", ...). GET /api/permissions/effective carries no per-item
   * grants, so a verb with all_items === false still grants — UI is optimistic and the
   * backend rejects items outside the user's scope (per-item rights go through
   * POST /api/permissions/authorize). Pass requireAllItems only for a deliberately strict check.
   */
  /**
   * The contract has no read verb: a feature with access === true is readable. This is the
   * read-level check; verb-level rights go through hasCapability.
   */
  public hasFeatureAccess(feature: FeatureDetail): boolean {
    const entry: EffectivePermissionEntry | undefined = this.authService.getEffectivePermissions()[feature];
    return entry?.access === true;
  }

  public hasCapability(
    feature: FeatureDetail,
    action: PermissionActionValue,
    options?: { requireAllItems?: boolean }
  ): boolean {
    const entry: EffectivePermissionEntry | undefined = this.authService.getEffectivePermissions()[feature];
    if (entry?.access !== true) {
      return false;
    }
    if (options?.requireAllItems && entry.all_items !== true) {
      return false;
    }
    return PermissionService.actionGranted(entry.capabilities, action);
  }

  private static actionGranted(capabilities: Record<string, string[]> | undefined, action: PermissionActionValue): boolean {
    return Object.values(capabilities ?? {}).some((actions: string[] | undefined) => !!actions?.includes(action));
  }

  /**
   * PLATFORM_ADMIN's login action grants access to the MTM Admin Console (menu entry,
   * /admin routes, console SSO) — it is NOT a bypass for CORE_* capability gates:
   * admin accounts carry the CORE_* verbs in their own /effective response.
   */
  public isPlatformAdmin(): boolean {
    return this.hasCapability(FeatureDetail.PLATFORM_ADMIN, PermissionAction.LOGIN);
  }

  /** ADMIN_STUDIO's login action ("Can sign in" under Applications) — the Admin Studio menu entry. */
  public canSignInAdminStudio(): boolean {
    return this.hasCapability(FeatureDetail.ADMIN_STUDIO, PermissionAction.LOGIN);
  }

  public canManageCompanyUsers(): boolean {
    return this.hasCapability(FeatureDetail.CORE_COMPANY, PermissionAction.MANAGE_USERS);
  }

  public canUpdateCompany(): boolean {
    return this.hasCapability(FeatureDetail.CORE_COMPANY, PermissionAction.UPDATE);
  }

  public canCreateWorkspace(): boolean {
    return this.hasCapability(FeatureDetail.CORE_WORKSPACE, PermissionAction.CREATE);
  }

  public canUpdateWorkspace(): boolean {
    return this.hasCapability(FeatureDetail.CORE_WORKSPACE, PermissionAction.UPDATE);
  }

  public canDeleteWorkspace(): boolean {
    return this.hasCapability(FeatureDetail.CORE_WORKSPACE, PermissionAction.DELETE);
  }

  /** Union of the two account-management capabilities the legacy S authority conflated. */
  public isAccountManager(): boolean {
    return this.canCreateWorkspace() || this.canManageCompanyUsers();
  }

  public isPlatformAdminOrAccountManager(): boolean {
    return this.isPlatformAdmin() || this.isAccountManager();
  }

  public canCreateProject(): boolean {
    return this.hasCapability(FeatureDetail.CORE_PROJECT, PermissionAction.CREATE);
  }

  public canUpdateProject(): boolean {
    return this.hasCapability(FeatureDetail.CORE_PROJECT, PermissionAction.UPDATE);
  }

  public canDeleteProject(): boolean {
    return this.hasCapability(FeatureDetail.CORE_PROJECT, PermissionAction.DELETE);
  }

  /**
   * Batch per-item CORE_PROJECT resolution — the async counterpart to hasCapability's global,
   * all-items-optimistic check. Hardcoded to CORE_PROJECT like the other named wrappers hardcode
   * their feature; a single fetch returns every action for an item, so the action itself is left
   * to hasItemCapability() rather than baked into several near-duplicate methods that would each
   * re-fetch the same data. Never redirects or gates navigation — see ItemPermissionsService.
   */
  public getProjectItemPermissions(itemUuids: string[]): Observable<ItemPermissionsResponse> {
    return this.itemPermissionsService.getItemPermissions(itemUuids, FeatureDetail.CORE_PROJECT);
  }

  /** Extracts one action's grant from a getProjectItemPermissions() entry. `entry` undefined
   *  (not yet fetched, or the id was absent from the response) denies. */
  public hasItemCapability(entry: ItemPermissionEntry | undefined, action: PermissionActionValue): boolean {
    if (entry?.access !== true) {
      return false;
    }
    return PermissionService.actionGranted(entry.capabilities, action);
  }

  public canCreateCampaign(): boolean {
    return this.hasCapability(FeatureDetail.CORE_CAMPAIGN, PermissionAction.CREATE);
  }

  public canUpdateCampaign(): boolean {
    return this.hasCapability(FeatureDetail.CORE_CAMPAIGN, PermissionAction.UPDATE);
  }

  /** The confirmed CORE_CAMPAIGN contract (create/update/delete) has no milestone verb — milestone editing rides on update. */
  public canEditCampaignMilestones(): boolean {
    return this.canUpdateCampaign();
  }

  public canDeleteCampaign(): boolean {
    return this.hasCapability(FeatureDetail.CORE_CAMPAIGN, PermissionAction.DELETE);
  }

  public canAccessCompanyProfile(): boolean {
    return this.hasFeatureAccess(FeatureDetail.CORE_COMPANY);
  }

  public canCreateBrand(): boolean {
    return this.hasCapability(FeatureDetail.CORE_BRAND, PermissionAction.CREATE);
  }

  public canUpdateBrand(): boolean {
    return this.hasCapability(FeatureDetail.CORE_BRAND, PermissionAction.UPDATE);
  }

  public canDeleteBrand(): boolean {
    return this.hasCapability(FeatureDetail.CORE_BRAND, PermissionAction.DELETE);
  }

  /** The CORE_BRAND contract has no manage verb — management access is any of create/update/delete. */
  public canManageBrand(): boolean {
    return this.canCreateBrand() || this.canUpdateBrand() || this.canDeleteBrand();
  }

  /**
   * Validation rights ("approve assets") live on the project's validators attribute. The global
   * effective-permissions response carries no such verb, but the per-item batch response does
   * (PermissionAction.APPROVE_ASSETS) — callers holding a fetched entry combine the two additively,
   * as in ProjectPermissionsComponent.resolveValidationRight.
   */
  public isProjectValidator(project: { validators?: string[] } | null | undefined): boolean {
    const username = this.authService.getAuthUserName();
    return !!username && !!project?.validators?.includes(username);
  }
}
